"use client";

import { cn } from "@/shared/libs/commonUtils";
import { type HTMLAttributes, type Ref, useCallback } from "react";
import { useCarouselSetup } from "./useCarouselSetup";
import { CarouselContext } from "./CarouselContext";
import CarouselItem from "./CarouselItem";
import CarouselNext from "./CarouselNext";
import CarouselPrevious from "./CarouselPrevious";
import CarouselContent from "./CarouselContent";
import type { CarouselProps } from "@/types/components/carousel";

interface ICarouselProps extends HTMLAttributes<HTMLDivElement>, CarouselProps {
  ref?: Ref<HTMLDivElement>;
}

function Carousel({
  ref,
  orientation = "horizontal",
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: ICarouselProps) {
  const carouselSetup = useCarouselSetup({
    opts,
    plugins,
    orientation,
    setApi,
  });

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        carouselSetup.scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        carouselSetup.scrollNext();
      }
    },
    [carouselSetup.scrollPrev, carouselSetup.scrollNext],
  );

  return (
    <CarouselContext.Provider value={carouselSetup}>
      <div
        ref={ref}
        onKeyDownCapture={handleKeyDown}
        className={cn("relative", className)}
        // role="region"
        aria-roledescription="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

export {
  Carousel,
  CarouselItem,
  CarouselContent,
  CarouselNext,
  CarouselPrevious,
};
